interface SegmentedToggleOption<T extends string> {
    value: T;
    label: string;
}

interface SegmentedToggleProps<T extends string> {
    value: T;
    options: readonly [SegmentedToggleOption<T>, SegmentedToggleOption<T>];
    onChange: (value: T) => void;
    disabled?: boolean;
    /** `sm` is 36px (On/Off and the settings view switch). `md` is 40px (Active/Inactive). */
    size?: 'md' | 'sm';
    minWidth?: number;
    /** When false, each pill hugs its label (Every setting / In this template). */
    equal?: boolean;
}

export default function SegmentedToggle<T extends string>({
    value,
    options,
    onChange,
    disabled = false,
    size = 'md',
    minWidth,
    equal = true,
}: SegmentedToggleProps<T>) {
    const sm = size === 'sm';
    return (
        <div
            className="inline-flex items-center"
            role="tablist"
            style={{
                height: sm ? 36 : 40,
                minWidth: equal ? (minWidth ?? (sm ? 150 : 200)) : undefined,
                padding: 3,
                gap: 3,
                boxSizing: 'border-box',
                borderRadius: 'var(--cpm-radius-10)',
                background: 'var(--cpm-surface-skeleton)',
                border: '1px solid var(--cpm-border-default)',
            }}
        >
            {options.map((option) => {
                const active = option.value === value;
                return (
                    <button
                        key={option.value}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        disabled={disabled}
                        onClick={() => onChange(option.value)}
                        className={disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}
                        style={{
                            flex: equal ? 1 : '0 0 auto',
                            height: sm ? 30 : 32,
                            padding: '0 13px',
                            whiteSpace: 'nowrap',
                            border: 'none',
                            borderRadius: 'var(--cpm-radius-8)',
                            background: active ? '#fff' : 'transparent',
                            color: active ? 'var(--cpm-brand-navy)' : 'var(--cpm-text-secondary)',
                            boxShadow: active ? 'var(--cpm-shadow-pill)' : 'none',
                            fontFamily: 'var(--cpm-font-sans)',
                            fontSize: 12.5,
                            fontWeight: 800,
                        }}
                    >
                        {option.label}
                    </button>
                );
            })}
        </div>
    );
}
